import { AxiosProgressEvent } from "axios";
import {
    IApiResponse,
    IPaginatedApiResponse,
} from "../../../utils/types/api-response";
import { handleRemoveEmptyParamFromQueryParams } from "../../../utils/helpers";
import {
    IClaimForRemit,
    IClaimsFilters,
    ICreateDraft,
    IGetClaimFilter,
    IUpdateClaimStatus,
    IBulkUpdateClaims,
    IUpdateClaimContent,
    IBulkCreateClaim,
    IClaimSubmissionHistoryFilters,
    IChangeInsurance,
    IGetClaimsAttachment,
    IDeleteClaimAttachment,
    IChangeClaimVoidStatus,
    IUpdateClaimsActivity,
    IChangeClaimsProvider,
    ICreateBulkParentClaimResponse,
    IUpdateBulkParentClaim,
    IUpdateBulkParentClaimResponse,
    ISplitClaimsToNewClaimResponse,
} from "../types/claims.types";
import axios from "../../../utils/axios";

// Endpoint to get a single claim
export async function getSingleClaim(filters: IGetClaimFilter) {
    const response = await axios.get(`/claims`, {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });

    return response.data;
}

export async function updateClaimStatus(payload: IUpdateClaimStatus) {
    const response = await axios.patch(`/claims`, {
        ...payload,
    });
    return response.data;
}

export async function getClaimsListsStats(filters: IClaimsFilters) {
    const response = await axios.get("/claims/stats", {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });
    return response.data;
}

export async function createDraftClaim(payload: ICreateDraft) {
    const response = await axios.post(`/claims`, {
        ...payload,
    });
    return response.data;
}

export async function bulkCreateClaims(payload: IBulkCreateClaim) {
    const response = await axios.post(`/claims/bulk-regenerate`, payload);
    return response.data;
}

export async function createBulkParentClaim(payload: IBulkCreateClaim) {
    const response = await axios.post<
        IApiResponse<ICreateBulkParentClaimResponse>
    >(`/claims/bulk-create`, payload);
    return response.data;
}

export async function fetchClaims(filters: IClaimsFilters) {
    const response = await axios.get(`/claims/search`, {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });
    return response.data;
}

export async function fetchClaimsForRemit(filters: IClaimsFilters) {
    const response = await axios.get<IPaginatedApiResponse<IClaimForRemit[]>>(
        `/claims/search-for-remit`,
        {
            params: handleRemoveEmptyParamFromQueryParams(filters),
        }
    );
    return response.data;
}

export async function getParentClaim(parentClaimId: string) {
    const response = await axios.get(`/claims/parent/${parentClaimId}`);
    return response.data;
}

export async function updateBulkParentClaim(
    parentClaimId: string,
    payload: IUpdateBulkParentClaim
) {
    const response = await axios.patch<
        IApiResponse<IUpdateBulkParentClaimResponse>
    >(`/claims/parent/${parentClaimId}`, payload);
    return response.data;
}

export async function fetchClaimsAttachments(params: IGetClaimsAttachment) {
    const response = await axios.get(`/claim/attachments`, {
        params: handleRemoveEmptyParamFromQueryParams(params),
    });
    return response.data;
}

export async function bulkUpdateClaims(payload: IBulkUpdateClaims) {
    const response = await axios.post(`/claims/status`, {
        ...payload,
    });
    return response.data;
}

export async function deleteClaims(claimIds: string[]) {
    const response = await axios.delete(`/claims`, {
        data: { claim_ids: claimIds },
    });
    return response.data;
}

export async function deleteClaimsAttachments(payload: IDeleteClaimAttachment) {
    const response = await axios.delete(`/claim/attachments`, {
        data: { ...payload },
    });
    return response.data;
}

export async function checkClaimStatus(claimIds: string[]) {
    const response = await axios.post(`/claims/status-checks`, {
        claim_ids: claimIds,
    });
    return response.data;
}

export async function updateClaimContent(payload: IUpdateClaimContent) {
    const response = await axios.post(`/claims/content`, payload);

    return response.data;
}

export async function fetchClaimSubmissionHistory(
    claimId: string,
    filters: IClaimSubmissionHistoryFilters
) {
    const response = await axios.get(`/claims/${claimId}/submission-history`, {
        params: handleRemoveEmptyParamFromQueryParams(filters),
    });

    return response.data;
}

export async function createManualClaim(
    payload: FormData,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
) {
    const response = await axios.post(`/manual-claims`, payload, {
        onUploadProgress,
    });
    return response.data;
}

// Endpoint to edit manual claim
export async function editManualClaim(
    claimId: string,
    payload: FormData,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
) {
    const response = await axios.patch(`/manual-claims/${claimId}`, payload, {
        onUploadProgress,
    });

    return response.data;
}

// Endpoint to change insurance provider
export async function changeInsuranceProvider(
    noteId: string,
    payload: IChangeInsurance
) {
    const response = await axios.post(
        `/client/session-note/${noteId}/change-insurance-provider`,
        payload
    );
    return response.data;
}

// Endpoint to void claim
export async function changeClaimVoidStatus(data: IChangeClaimVoidStatus) {
    const response = await axios.post("/claims/void", data);
    return response.data;
}

export async function updateClaimsActivity(payload: IUpdateClaimsActivity) {
    const response = await axios.post("/claim/activity", payload);
    return response.data;
}

export async function fetchRejectionReasons() {
    const response = await axios.get("/claims/rejection-reasons");
    return response.data;
}

export async function changeClaimsProvider(payload: IChangeClaimsProvider) {
    const response = await axios.post(`claims/change-provider`, payload);
    return response.data;
}

export async function removeClaimFromBulkClaim(
    parentClaimId: string,
    claimId: string
) {
    return updateBulkParentClaim(parentClaimId, {
        claim_ids_to_remove: [claimId],
    });
}

export async function addNotesToBulkClaim(
    parentClaimId: string,
    noteIds: string[]
) {
    return updateBulkParentClaim(parentClaimId, {
        note_ids_to_add: noteIds,
    });
}

export async function removeClaimsFromBulkClaim(
    parentClaimId: string,
    claimIds: string[]
) {
    return updateBulkParentClaim(parentClaimId, {
        claim_ids_to_remove: claimIds,
    });
}

export async function splitClaimsToNewClaim(
    parentClaimId: string,
    claimIds: string[]
) {
    const response = await axios.post<
        IApiResponse<ISplitClaimsToNewClaimResponse>
    >(`/claims/parent/${parentClaimId}/split`, {
        claim_ids: claimIds,
    });
    return response.data;
}
